/**
 * Client-side price range filter for the collection filter drawer (snippets/collection-price-filter.liquid).
 *
 * The default collection page filters through tags in the URL, which cannot express a price range, and
 * theme.js only reloads the grid when the tags change. So the range is applied here by hiding the
 * product cards (data-price, in cents) outside it. It relies on the endless scroll setting, which
 * renders every product of the collection in the DOM.
 *
 * Two instances can exist: the drawer one (committed by the drawer's Apply button) and the desktop
 * sidebar one (data-price-live, committed as soon as a control is released). They mirror each other.
 * Reset clears the range along with the tags.
 */
(() => {
  const roots = Array.from(document.querySelectorAll('[data-price-filter]'))
  if (!roots.length) return

  const PRODUCTS_SELECTOR = '.CollectionInner__Products'
  const CARD_SELECTOR = '.product_blk[data-price]'
  const HIDDEN_CLASS = 'is-price-hidden'
  const PARAM_MIN = 'price_min'
  const PARAM_MAX = 'price_max'

  const productsElement = document.querySelector(PRODUCTS_SELECTOR)
  if (!productsElement) return

  const instances = roots
    .map((root) => ({
      root,
      live: root.hasAttribute('data-price-live'),
      slider: root.querySelector('[data-price-slider]'),
      rangeMin: root.querySelector('[data-price-range="min"]'),
      rangeMax: root.querySelector('[data-price-range="max"]'),
      inputMin: root.querySelector('[data-price-input="min"]'),
      inputMax: root.querySelector('[data-price-input="max"]')
    }))
    .filter((item) => item.slider && item.rangeMin && item.rangeMax && item.inputMin && item.inputMax)

  if (!instances.length) return

  let bounds = { min: 0, max: 0 }
  let applied = null // { min, max } in whole currency units, null when no range is applied

  /** Cheapest and most expensive product of the page, rounded outwards to whole units. */
  function readBounds() {
    const prices = Array.from(productsElement.querySelectorAll(CARD_SELECTOR))
      .map((card) => Number(card.dataset.price) / 100)
      .filter((price) => Number.isFinite(price))

    if (!prices.length) return { min: 0, max: 0 }

    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) }
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  function parseValue(input, fallback) {
    const number = parseInt(input.value.replace(/[^0-9]/g, ''), 10)
    return Number.isFinite(number) ? number : fallback
  }

  /** Paints the slider track between both thumbs and mirrors the values into the fields of every instance. */
  function render(min, max) {
    const span = bounds.max - bounds.min || 1

    instances.forEach(({ slider, rangeMin, rangeMax, inputMin, inputMax }) => {
      rangeMin.value = min
      rangeMax.value = max
      inputMin.value = min
      inputMax.value = max

      slider.style.setProperty('--pdm-price-start', `${((min - bounds.min) / span) * 100}%`)
      slider.style.setProperty('--pdm-price-end', `${((max - bounds.min) / span) * 100}%`)
    })
  }

  function setBounds(next) {
    bounds = next

    instances.forEach(({ root, rangeMin, rangeMax }) => {
      ;[rangeMin, rangeMax].forEach((range) => {
        range.min = bounds.min
        range.max = bounds.max
      })

      // A collection with a single price has nothing to filter by.
      root.hidden = bounds.min >= bounds.max
    })
  }

  /** Range stored in the URL, or null when absent or invalid. */
  function readUrl() {
    const params = new URLSearchParams(window.location.search)
    const min = parseInt(params.get(PARAM_MIN), 10)
    const max = parseInt(params.get(PARAM_MAX), 10)

    if (!Number.isFinite(min) && !Number.isFinite(max)) return null

    return {
      min: Number.isFinite(min) ? min : bounds.min,
      max: Number.isFinite(max) ? max : bounds.max
    }
  }

  /**
   * Mirrors the applied range into the query string. theme.js rewrites the URL on every tag or sort
   * change keeping only sort_by, so this runs again after each of them. history.state is kept as is:
   * theme.js reads state.path on popstate.
   */
  function writeUrl() {
    const url = new URL(window.location.href)

    url.searchParams.delete(PARAM_MIN)
    url.searchParams.delete(PARAM_MAX)

    if (applied) {
      url.searchParams.set(PARAM_MIN, applied.min)
      url.searchParams.set(PARAM_MAX, applied.max)
    }

    window.history.replaceState(window.history.state, '', url)
  }

  /** Puts the controls back on the applied range (or the full range). */
  function restore() {
    const min = applied ? clamp(applied.min, bounds.min, bounds.max) : bounds.min
    const max = applied ? clamp(applied.max, bounds.min, bounds.max) : bounds.max

    render(min, max)
  }

  function showResetButtons() {
    const hasTags = Boolean(window.location.pathname.match(/\/collections\/[^/]+\/.+/))

    document.querySelectorAll('[data-action="reset-tags"]').forEach((button) => {
      button.style.display = applied || hasTags ? 'block' : 'none'
    })
  }

  /** Hides the cards outside the applied range. */
  function filterProducts() {
    const container = productsElement.querySelector('#product_list_container')

    productsElement.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const price = Number(card.dataset.price) / 100
      const inRange = !applied || (price >= applied.min && price <= applied.max)

      card.classList.toggle(HIDDEN_CLASS, !inRange)

      // Endless scroll only reveals cards on scroll; a short filtered list would never get there.
      if (applied && inRange) card.classList.add('product_active')
    })

    if (container) container.classList.toggle('is-price-filtered', Boolean(applied))
    productsElement.classList.toggle('is-price-filtered', Boolean(applied))
  }

  /** Applies the range held by the given instance's fields. */
  function commit({ inputMin, inputMax }) {
    const min = parseValue(inputMin, bounds.min)
    const max = parseValue(inputMax, bounds.max)
    const isFullRange = min <= bounds.min && max >= bounds.max

    applied = isFullRange ? null : { min: Math.min(min, max), max: Math.max(min, max) }

    restore()
    filterProducts()
    writeUrl()

    // theme.js hides the reset button after its own commit when no tag is active.
    window.setTimeout(showResetButtons, 0)
  }

  function reset() {
    applied = null
    restore()
    filterProducts()
    writeUrl()
  }

  instances.forEach((instance) => {
    const { live, rangeMin, rangeMax, inputMin, inputMax } = instance

    rangeMin.addEventListener('input', () => {
      render(Math.min(Number(rangeMin.value), Number(rangeMax.value)), Number(rangeMax.value))
    })

    rangeMax.addEventListener('input', () => {
      render(Number(rangeMin.value), Math.max(Number(rangeMax.value), Number(rangeMin.value)))
    })

    // The sidebar has no Apply button: commit once the thumb is released ("change").
    if (live) {
      ;[rangeMin, rangeMax].forEach((range) => range.addEventListener('change', () => commit(instance)))
    }

    ;[inputMin, inputMax].forEach((input) => {
      input.addEventListener('change', () => {
        const min = clamp(parseValue(inputMin, bounds.min), bounds.min, bounds.max)
        const max = clamp(parseValue(inputMax, bounds.max), bounds.min, bounds.max)

        render(Math.min(min, max), Math.max(min, max))
        if (live) commit(instance)
      })

      // Numbers only; the visible "$" is a separate element.
      input.addEventListener('input', () => {
        input.value = input.value.replace(/[^0-9]/g, '')
      })
    })
  })

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-action="apply-tags"]')) {
      // Commit what is typed even if the field never fired "change" (e.g. Apply tapped straight away).
      commit(instances.find((item) => !item.live) || instances[0])
      return
    }

    if (event.target.closest('[data-action="reset-tags"]')) {
      reset()
      return
    }

    // Opening the drawer shows what is applied, in case it was closed without applying.
    if (event.target.closest('[data-drawer-id="collection-filter-drawer"][data-action="open-drawer"]')) {
      restore()
    }
  })

  // Tag filtering replaces the grid through AJAX: recompute the bounds and keep the range applied.
  // Only class changes happen on the cards, so this observer does not trigger itself.
  new MutationObserver(() => {
    const next = readBounds()

    setBounds(next)
    if (applied && (applied.max < next.min || applied.min > next.max)) applied = null

    restore()
    filterProducts()
    writeUrl()
  }).observe(productsElement, { childList: true })

  setBounds(readBounds())

  // Range from the URL (reload or shared link), clamped to what this collection offers.
  const fromUrl = readUrl()

  if (fromUrl) {
    const min = clamp(Math.min(fromUrl.min, fromUrl.max), bounds.min, bounds.max)
    const max = clamp(Math.max(fromUrl.min, fromUrl.max), bounds.min, bounds.max)

    applied = min <= bounds.min && max >= bounds.max ? null : { min, max }
  }

  restore()
  filterProducts()
  window.setTimeout(showResetButtons, 0)
})()
